export type ChatImage = { filename: string; url: string };
/** A PDF sent as a file to models that read PDFs; `text` is its text layer as fallback. */
export type ChatFile = { filename: string; url: string; text: string };
export type AttachmentPreview = {
  filename: string;
  text: string;
  image?: ChatImage;
  file?: ChatFile;
  /** Pages of a scanned PDF, rendered as images for vision models. */
  images?: ChatImage[];
};

import type { DocumentProcessingStatus } from './lib/documentProcessing';
/**
 * Typed wrapper around fetch for the chat backend. Reads the CSRF cookie
 * (set by the server on login) and echoes it as `x-csrf-token` on every
 * non-GET request — matching the double-submit pattern enforced by the
 * backend's CSRF middleware.
 */

export type ApiUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  /** Profile photo from the Microsoft sign-in; initials are shown without one. */
  photoUrl?: string | null;
  role: 'admin' | 'member';
  agentsEnabled: boolean;
  preferredLanguage: 'nl' | 'en' | 'fr';
};

export type Product = {
  profile: 'full' | 'light';
  /** Where the gateway admin console lives (not the chat's own /admin page). */
  adminUrl?: string;
  capabilities: {
    modelSelection: boolean;
    knowledgeSelection: boolean;
    skills: boolean;
    projects: boolean;
    artifacts: boolean;
    automations: boolean;
    agents: boolean;
    advancedAdmin: boolean;
    externalIntegrations: boolean;
  };
};

export type Model = {
  id: string;
  object: 'model';
  created: number;
  owned_by: string;
  scoreIcons?: {
    performance: 'bolt' | 'star' | 'coin' | 'circle' | 'diamond';
    quality: 'bolt' | 'star' | 'coin' | 'circle' | 'diamond';
    cost: 'bolt' | 'star' | 'coin' | 'circle' | 'diamond';
  };
  scores?: { performance: number | null; quality: number | null; cost: number | null };
};

export type DatasetOwner = {
  name: string;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
};

export type DatasetGroup = {
  slug: string;
  name: string;
  description: string | null;
  owner: DatasetOwner | null;
  datasets: Array<{
    slug: string;
    name: string;
    description: string | null;
    parameterSchema: unknown;
  }>;
};

export type SkillDraft = {
  name: string;
  description: string;
  body: string;
  outputType?: 'text' | 'interactive_html' | 'table' | 'todo';
  datasetGroupSlugs?: string[];
};
export type SkillSharingGroup = { id: string; name: string };

export type Skill = {
  personal?: {
    canEdit: boolean;
    sharedGroupId: string | null;
    version: number;
    /** Only present once the gateway stores it (see skills report). */
    allowTeamEdits?: boolean;
  };
  /** Phrases that trigger the skill; only present once the gateway stores them. */
  examples?: string[];
  id: string;
  teamId: string;
  slug: string;
  name: string;
  description: string;
  domain: string;
  effort: string;
  priority?: number;
  tags: string[];
  model: string | null;
  datasetGroupSlugs: string[];
  outputType?: 'text' | 'interactive_html' | 'table' | 'todo';
  inputs: { name: string; type: string }[];
  body: string;
  updatedAt: string;
};

export type ConversationListItem = {
  id: string;
  title: string;
  modelId: string;
  projectId: string | null;
  selectedSkillSlug: string | null;
  selectedSkillSlugs: string[];
  enabledDatasetGroups: string[];
  updatedAt: string;
  messageCount: number;
};

export type StoredMessage = {
  id: string;
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: {
    text?: string;
    images?: ChatImage[];
    tool_calls?: Array<{
      id: string;
      type: 'function';
      function: { name: string; arguments: string };
    }>;
    tool_call_id?: string;
    output?: unknown;
  };
  tokensIn: number | null;
  tokensOut: number | null;
  finishReason: string | null;
  latencyMs: number | null;
  datasetGroupSlugs: string[];
  createdAt: string;
};

export type ConversationDetail = {
  id: string;
  /** The server is still making an answer for this conversation. */
  generating?: boolean;
  version: number;
  title: string;
  modelId: string;
  projectId: string | null;
  systemPrompt: string | null;
  selectedSkillSlug: string | null;
  selectedSkillSlugs: string[];
  /** Skill the chat picked by itself for the latest turn (`teamId/slug`). */
  autoSkillKey?: string | null;
  enabledDatasetGroups: string[];
  createdAt: string;
  updatedAt: string;
  messages: StoredMessage[];
};

export type ConversationTools = {
  sqliteDatabases: Array<{
    id: string;
    filename: string;
  }>;
};

export type Automation = {
  id: string;
  kind: 'scheduled_task' | 'agent';
  title: string;
  skillSlug: string;
  prompt: string;
  modelId: string;
  enabledDatasetGroups: string[];
  everyMinutes: number;
  scheduleTime: string | null;
  timeZone: string | null;
  status: 'enabled' | 'paused';
  notifyOnFinish: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AutomationRun = {
  id: string;
  automationId: string;
  conversationId: string | null;
  status: 'queued' | 'running' | 'completed' | 'error';
  resultPreview: string | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  seenAt: string | null;
};

/** A run that finished after the poll cursor, for the "task finished" notification. */
export type FinishedRun = {
  id: string;
  automationId: string;
  title: string;
  conversationId: string | null;
  status: 'completed' | 'error';
  completedAt: string;
};

export type AgentActionApproval = {
  id: string;
  automationId: string;
  teamRunId: string | null;
  conversationId: string | null;
  toolName: string;
  actionKind: 'send' | 'write' | 'delete';
  datasetGroupSlug: string | null;
  arguments: unknown;
  status:
    | 'pending'
    | 'approved'
    | 'executing'
    | 'executed'
    | 'rejected'
    | 'expired'
    | 'error'
    | 'unknown';
  error: string | null;
  createdAt: string;
  expiresAt: string;
  approvedAt: string | null;
  executionStartedAt: string | null;
  completedAt: string | null;
  verifiedAt: string | null;
};

export type AgentTeam = {
  id: string;
  title: string;
  prompt: string;
  everyMinutes: number;
  status: 'enabled' | 'paused';
  nextRunAt: string;
  lastRunAt: string | null;
  members: Array<{
    agentId: string;
    title: string;
    role: 'coordinator' | 'specialist';
    position: number;
  }>;
};

export type AgentTeamRun = {
  id: string;
  teamId: string;
  conversationId: string | null;
  status: 'running' | 'completed' | 'error';
  resultPreview: string | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  handoffs: Array<{
    id: string;
    fromAgentId: string | null;
    toAgentId: string;
    position: number;
    status: 'running' | 'completed' | 'error';
    conversationId: string | null;
    output: string | null;
    error: string | null;
  }>;
};

/** This month's usage per model (gateway audit log), for the account menu. */
export type UsageBreakdown = {
  budgetUsd: number | null;
  spentUsd: number;
  models: Array<{
    model: string;
    requests: number;
    tokensIn: number;
    tokensOut: number;
    costUsd: number;
  }>;
};

export type UsageSummary = {
  allTime: { messages: number; tokensIn: number; tokensOut: number };
  currentMonth: { messages: number; tokensIn: number; tokensOut: number };
};

export type Project = {
  id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  defaultDatasetGroups: string[];
  /** 'shared' when shared with everyone or with at least one group. */
  visibility: 'private' | 'shared';
  sharedWithEveryone: boolean;
  /** The groups it is shared with; only filled in for the owner. */
  sharedGroupIds: string[];
  /** Skills switched on by default in each new conversation of the project. */
  skillSlugs: string[];
  /** The user owns the project; others may use it but not change it. */
  isOwner: boolean;
  /** Who shared the project; null for the owner. */
  ownerName: string | null;
  /** Present on the project list only. */
  documentCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type ProjectDocument = {
  id: string;
  projectId: string;
  filename: string;
  displayName: string | null;
  description: string | null;
  tags: string[];
  active: boolean;
  version: number;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  status: string;
  errorMessage: string | null;
  indexedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProjectDetail = Project & { documents: ProjectDocument[] };

export type ProjectDocumentPreview = {
  id: string;
  filename: string;
  displayName: string | null;
  status: string;
  text: string;
  truncated: boolean;
};

export type ProjectPolicy = {
  maxUploadBytes: number;
  maxDocuments: number;
  maxStorageBytes: number;
  allowedExtensions: string[] | null;
  ocrEnabled: boolean;
};

export type ToolFailure = {
  messageId: string;
  conversationId: string;
  conversationTitle: string;
  modelId: string;
  userEmail: string;
  toolCallId: string | null;
  toolName: string | null;
  error: unknown;
  createdAt: string;
};

export type MicrosoftMailStatus = {
  configured: boolean;
  delegatedEnabled: boolean;
  appOnlyEnabled: boolean;
  appOnlyAllowedMailboxes: string[];
  delegatedConnected: boolean;
  delegatedEmail: string | null;
  delegatedScopes: string[];
};

export type AzureDevOpsConnection = {
  configured: boolean;
  enabled: boolean;
  organization: string | null;
  personalAccessTokenAllowed: boolean;
  personalAccessToken: { accountName: string | null; savedAt: string } | null;
};

export type LeexiConnection = {
  configured: boolean;
  enabled: boolean;
  personalKeysAllowed: boolean;
  companyKeyConfigured: boolean;
  personalKey: { label: string | null; savedAt: string } | null;
};

export type MicrosoftConnectionStatus = MicrosoftMailStatus & {
  connected: boolean;
  /** The connection lacks permissions added later (mail content, calendar); connect again. */
  reconnectRequired?: boolean;
  accountEmail: string | null;
  scopes: string[];
  capabilities: {
    mail: {
      configured: boolean;
      delegatedEnabled: boolean;
      appOnlyEnabled: boolean;
      delegatedConnected: boolean;
      delegatedEmail: string | null;
      delegatedScopes: string[];
    };
    sharePoint: {
      delegatedConnected: boolean;
      delegatedScopes: string[];
    };
    oneDrive: {
      delegatedConnected: boolean;
      delegatedScopes: string[];
    };
    teams: {
      delegatedConnected: boolean;
      delegatedScopes: string[];
    };
  };
};

/** The user's own record of the Chat welcome tour; null until they finish or skip it. */
export type OnboardingRecord = {
  tour: 'chat';
  version: number;
  status: 'completed' | 'skipped';
  updatedAt: string;
};

export type MicrosoftMailSendDraft = {
  to: string[];
  cc: string[];
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
};

class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function readCookie(name: string): string | null {
  const target = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) return trimmed.slice(target.length);
  }
  return null;
}

function readCsrfCookie(): string | null {
  return __PRODUCT_PROFILE__ === 'light'
    ? readCookie('__Host-ncl_csrf')
    : (readCookie('__Host-nc_csrf') ?? readCookie('nc_csrf'));
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCsrfCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  const init: RequestInit = { method, headers, credentials: 'include', signal: signal ?? null };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = text || `request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text) as { error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Message of an upload a proxy refused for its size; pages show their own text for it. */
export const UPLOAD_TOO_LARGE = 'upload_too_large';

async function uploadRequest<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const csrf = readCsrfCookie();
  if (csrf) headers['x-csrf-token'] = csrf;
  const res = await fetch(path, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
    signal: signal ?? null,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // A proxy or the server cut off a too large upload before the app could answer
    // ("413", "502 Bad Gateway" as HTML): say what happened instead.
    if ([413, 502, 504].includes(res.status) && !text.trim().startsWith('{')) {
      throw new ApiError(res.status, UPLOAD_TOO_LARGE);
    }
    let message = text || `request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text) as { error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export const api = {
  async login(
    email: string,
    password: string,
  ): Promise<{ user: ApiUser & { allowedModels: string[]; allowedDatasetGroups: string[] } }> {
    return request('POST', '/api/auth/login', { email, password });
  },
  async ssoConfig(): Promise<{ enabled: boolean }> {
    return request('GET', '/api/auth/sso/config');
  },
  async logout(): Promise<void> {
    await request('POST', '/api/auth/logout', {});
  },
  async pushConfig(): Promise<{ enabled: boolean; publicKey?: string }> {
    return request('GET', '/api/push/config');
  },
  async pushSubscribe(subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
  }): Promise<void> {
    await request('POST', '/api/push/subscribe', subscription);
  },
  async pushUnsubscribe(endpoint: string): Promise<void> {
    await request('POST', '/api/push/unsubscribe', { endpoint });
  },
  async me(): Promise<{
    user: ApiUser;
    product: Product;
    /** Admin's chat attachment limits in bytes; null = no limit. */
    attachmentLimits?: { maxFileBytes: number | null; maxTotalBytes: number | null };
  }> {
    return request('GET', '/api/me');
  },
  async onboarding(): Promise<OnboardingRecord | null> {
    return request('GET', '/api/me/onboarding');
  },
  async recordOnboarding(input: {
    version: number;
    status: OnboardingRecord['status'];
  }): Promise<OnboardingRecord> {
    return request('POST', '/api/me/onboarding', input);
  },
  async listModels(): Promise<{ data: Model[] }> {
    return request('GET', '/api/models');
  },
  async listDatasetGroups(): Promise<{ data: DatasetGroup[] }> {
    return request('GET', '/api/dataset-groups');
  },
  async createPersonalSkill(input: SkillDraft): Promise<Skill> {
    return request('POST', '/api/personal-skills', input);
  },
  async updatePersonalSkill(
    id: string,
    input: SkillDraft & {
      sharedGroupId: string | null;
      expectedVersion: number;
      /** Sent only when the gateway reported the fields on the loaded skill. */
      examples?: string[];
      allowTeamEdits?: boolean;
    },
  ): Promise<Skill> {
    return request('PATCH', `/api/personal-skills/${encodeURIComponent(id)}`, input);
  },
  async deletePersonalSkill(id: string, expectedVersion: number): Promise<void> {
    return request(
      'DELETE',
      `/api/personal-skills/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`,
    );
  },
  async skillSharingGroups(): Promise<{ data: SkillSharingGroup[] }> {
    return request('GET', '/api/skill-sharing-groups');
  },
  async branchConversation(conversationId: string, messageId: string): Promise<{ id: string }> {
    return request('POST', `/api/conversations/${conversationId}/branch`, { messageId });
  },
  async draftSkill(conversationId: string, messageId: string): Promise<SkillDraft> {
    return request('POST', `/api/conversations/${encodeURIComponent(conversationId)}/skill-draft`, {
      messageId,
    });
  },

  /** The chat model writes or revises a skill from the user's description; nothing is saved. */
  async writeSkill(input: {
    request: string;
    modelId: string;
    current?: { name: string; description: string; body: string };
  }): Promise<SkillDraft> {
    return request('POST', '/api/skill-writer', input);
  },

  async listSkills(): Promise<{ data: Skill[] }> {
    return request('GET', '/api/skills');
  },
  async skillUsage(): Promise<{ since: string; data: Record<string, number> }> {
    return request('GET', '/api/skills/usage');
  },
  async usageBreakdown(): Promise<UsageBreakdown> {
    return request('GET', '/api/me/usage-breakdown');
  },
  async usage(): Promise<UsageSummary> {
    return request('GET', '/api/usage');
  },
  async microsoftConnectionStatus(): Promise<MicrosoftConnectionStatus> {
    return request('GET', '/api/microsoft/connection/status');
  },
  async disconnectMicrosoft(): Promise<void> {
    await request('POST', '/api/microsoft/connection/disconnect', {});
  },
  async connectMicrosoft(): Promise<{ authorizationUrl: string; expiresInSeconds: number }> {
    return request('POST', '/api/microsoft/connection/connect', {});
  },
  async azureDevOpsConnection(): Promise<AzureDevOpsConnection> {
    return (await request<{ data: AzureDevOpsConnection }>('GET', '/api/azure-devops/connection'))
      .data;
  },
  async saveAzureDevOpsToken(token: string): Promise<AzureDevOpsConnection> {
    return (
      await request<{ data: AzureDevOpsConnection }>('POST', '/api/azure-devops/connection', {
        token,
      })
    ).data;
  },
  async removeAzureDevOpsToken(): Promise<AzureDevOpsConnection> {
    return (
      await request<{ data: AzureDevOpsConnection }>('DELETE', '/api/azure-devops/connection')
    ).data;
  },
  async leexiConnection(): Promise<LeexiConnection> {
    return (await request<{ data: LeexiConnection }>('GET', '/api/leexi/connection')).data;
  },
  async saveLeexiKey(key: { keyId: string; keySecret: string }): Promise<LeexiConnection> {
    return (await request<{ data: LeexiConnection }>('POST', '/api/leexi/connection', key)).data;
  },
  async removeLeexiKey(): Promise<LeexiConnection> {
    return (await request<{ data: LeexiConnection }>('DELETE', '/api/leexi/connection')).data;
  },
  async microsoftMailStatus(): Promise<MicrosoftMailStatus> {
    return request('GET', '/api/microsoft/mail/status');
  },
  async connectMicrosoftMail(): Promise<{ authorizationUrl: string; expiresInSeconds: number }> {
    return request('POST', '/api/microsoft/mail/connect', {});
  },
  async sendMicrosoftMail(draft: MicrosoftMailSendDraft): Promise<unknown> {
    return request('POST', '/api/microsoft/mail/send', draft);
  },
  async listToolFailures(): Promise<{ data: ToolFailure[] }> {
    return request('GET', '/api/admin/tool-failures');
  },
  async projectPolicy(): Promise<ProjectPolicy> {
    return request('GET', '/api/project-policy');
  },
  async listConversations(
    opts: { limit?: number; perProject?: number } = {},
  ): Promise<{ data: ConversationListItem[] }> {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.perProject) params.set('per_project', String(opts.perProject));
    const query = params.toString();
    return request('GET', `/api/conversations${query ? `?${query}` : ''}`);
  },
  async searchConversations(opts: {
    q?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: ConversationListItem[]; total: number }> {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.projectId) params.set('project', opts.projectId);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    return request('GET', `/api/conversations/search?${params.toString()}`);
  },
  async projectConversationCounts(): Promise<{ data: Record<string, number> }> {
    return request('GET', '/api/conversations/project-counts');
  },
  /** B15: file and app blocks of recent answers, for the Files page. */
  async listArtifactFences(): Promise<{
    data: Array<{
      conversationId: string;
      conversationTitle: string;
      messageId: string;
      fences: string;
    }>;
  }> {
    return request('GET', '/api/conversations/artifacts');
  },
  async getConversation(id: string): Promise<ConversationDetail> {
    return request('GET', `/api/conversations/${id}`);
  },
  async stopConversation(id: string): Promise<{ stopped: boolean }> {
    return request('POST', `/api/conversations/${id}/stop`);
  },
  async getConversationTools(id: string): Promise<{ data: ConversationTools }> {
    return request('GET', `/api/conversations/${id}/tools`);
  },
  async createConversation(input: {
    modelId: string;
    title?: string;
    projectId?: string | null;
    systemPrompt?: string;
    selectedSkillSlug?: string | null;
    selectedSkillSlugs?: string[];
    enabledDatasetGroups?: string[];
  }): Promise<ConversationDetail> {
    return request('POST', '/api/conversations', input);
  },
  async patchConversation(
    id: string,
    patch: {
      expectedVersion: number;
      title?: string;
      modelId?: string;
      projectId?: string | null;
      selectedSkillSlug?: string | null;
      selectedSkillSlugs?: string[];
      enabledDatasetGroups?: string[];
      systemPrompt?: string | null;
    },
  ): Promise<void> {
    await request('PATCH', `/api/conversations/${id}`, patch);
  },
  async deleteConversation(id: string): Promise<void> {
    await request('DELETE', `/api/conversations/${id}`);
  },
  async listAutomations(agentMode = false): Promise<{
    data: Automation[];
    runs: AutomationRun[];
    unread: number;
    /** Tasks that need the user: failed latest run, or a finished run to open with notifications on. */
    attention?: number;
  }> {
    return request('GET', agentMode ? '/api/agents' : '/api/automations');
  },
  async createAutomation(
    input: {
      title: string;
      skillSlug: string;
      prompt: string;
      modelId: string;
      enabledDatasetGroups: string[];
      everyMinutes: number;
      scheduleTime?: string | null;
      timeZone?: string | null;
      notifyOnFinish?: boolean;
    },
    agentMode = false,
  ): Promise<Automation> {
    return request('POST', agentMode ? '/api/agents' : '/api/automations', input);
  },
  async patchAutomation(
    id: string,
    patch: Partial<{
      title: string;
      skillSlug: string;
      prompt: string;
      modelId: string;
      enabledDatasetGroups: string[];
      everyMinutes: number;
      scheduleTime: string | null;
      timeZone: string | null;
      status: 'enabled' | 'paused';
      notifyOnFinish: boolean;
    }>,
    agentMode = false,
  ): Promise<void> {
    await request('PATCH', `${agentMode ? '/api/agents' : '/api/automations'}/${id}`, patch);
  },
  async deleteAutomation(id: string, agentMode = false): Promise<void> {
    await request('DELETE', `${agentMode ? '/api/agents' : '/api/automations'}/${id}`);
  },
  async runAutomation(id: string, agentMode = false): Promise<AutomationRun> {
    return request('POST', `${agentMode ? '/api/agents' : '/api/automations'}/${id}/run`, {});
  },
  async markAutomationRunSeen(id: string, agentMode = false): Promise<void> {
    await request('POST', `${agentMode ? '/api/agents' : '/api/automations'}/runs/${id}/seen`, {});
  },
  /** Runs finished after `since`; `now` is the next cursor. Without `since` the list is empty. */
  async finishedRuns(
    agentMode: boolean,
    since: string | null,
  ): Promise<{ data: FinishedRun[]; now: string }> {
    const base = agentMode ? '/api/agents' : '/api/automations';
    return request(
      'GET',
      `${base}/runs/finished${since ? `?since=${encodeURIComponent(since)}` : ''}`,
    );
  },
  async listAgentTeams(): Promise<{ data: AgentTeam[]; runs: AgentTeamRun[] }> {
    return request('GET', '/api/agent-teams');
  },
  async createAgentTeam(input: {
    title: string;
    prompt: string;
    everyMinutes: number;
    coordinatorAgentId: string;
    specialistAgentIds: string[];
  }): Promise<AgentTeam> {
    return request('POST', '/api/agent-teams', input);
  },
  async patchAgentTeam(
    id: string,
    patch: Partial<{
      title: string;
      prompt: string;
      everyMinutes: number;
      coordinatorAgentId: string;
      specialistAgentIds: string[];
      status: 'enabled' | 'paused';
    }>,
  ): Promise<AgentTeam> {
    return request('PATCH', `/api/agent-teams/${id}`, patch);
  },
  async deleteAgentTeam(id: string): Promise<void> {
    await request('DELETE', `/api/agent-teams/${id}`);
  },
  async runAgentTeam(id: string): Promise<AgentTeamRun> {
    return request('POST', `/api/agent-teams/${id}/run`, {});
  },
  async listAgentActionApprovals(): Promise<{ data: AgentActionApproval[] }> {
    return request('GET', '/api/agent-action-approvals');
  },
  async approveAgentActionApproval(id: string): Promise<AgentActionApproval> {
    return request('POST', `/api/agent-action-approvals/${id}/approve`, {});
  },
  async resolveAgentActionApproval(
    id: string,
    outcome: 'executed' | 'not_executed',
  ): Promise<void> {
    await request('POST', `/api/agent-action-approvals/${id}/resolve`, { outcome });
  },
  async rejectAgentActionApproval(id: string): Promise<void> {
    await request('POST', `/api/agent-action-approvals/${id}/reject`, {});
  },
  async listProjects(): Promise<{ data: Project[] }> {
    return request('GET', '/api/projects');
  },
  async getProject(id: string): Promise<ProjectDetail> {
    return request('GET', `/api/projects/${id}`);
  },
  async createProject(input: {
    name: string;
    description?: string | null;
    instructions?: string | null;
    defaultDatasetGroups?: string[];
  }): Promise<Project> {
    return request('POST', '/api/projects', input);
  },
  async patchProject(
    id: string,
    patch: Partial<{
      name: string;
      description: string | null;
      instructions: string | null;
      defaultDatasetGroups: string[];
      sharedWithEveryone: boolean;
      sharedGroupIds: string[];
      skillSlugs: string[];
    }>,
  ): Promise<Project> {
    return request('PATCH', `/api/projects/${id}`, patch);
  },
  async deleteProject(id: string): Promise<void> {
    await request('DELETE', `/api/projects/${id}`);
  },
  async extractChatAttachment(
    id: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<AttachmentPreview> {
    const form = new FormData();
    form.set('file', file);
    return uploadRequest(`/api/conversations/${id}/attachments`, form, signal);
  },
  async transcriptionStatus(): Promise<{ available: boolean }> {
    return request('GET', '/api/transcribe/status');
  },
  async transcribeAudio(
    file: File,
    language: string,
    signal?: AbortSignal,
  ): Promise<{ text: string }> {
    const form = new FormData();
    form.set('file', file);
    form.set('language', language);
    return uploadRequest<{ text: string }>('/api/transcribe', form, signal);
  },
  async speechStatus(): Promise<{ available: boolean }> {
    return request('GET', '/api/speech/status');
  },
  /** An answer as a few sentences to say aloud (the model that writes titles makes it). */
  async speechSummary(
    text: string,
    language: string,
    model: string,
    signal?: AbortSignal,
  ): Promise<string> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      'content-type': 'application/json',
    };
    const csrf = readCsrfCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
    const res = await fetch('/api/speech/summary', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ text, language, model }),
      signal: signal ?? null,
    });
    if (!res.ok) throw new ApiError(res.status, `summary failed (${res.status})`);
    return ((await res.json()) as { text: string }).text;
  },
  /** One spoken clip of `input` (at most 4096 characters), in the voice the admin chose. */
  async speak(input: string, signal?: AbortSignal): Promise<Blob> {
    const headers: Record<string, string> = {
      accept: 'audio/*',
      'content-type': 'application/json',
    };
    const csrf = readCsrfCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
    const res = await fetch('/api/speech', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ input }),
      signal: signal ?? null,
    });
    if (!res.ok) throw new ApiError(res.status, `speech failed (${res.status})`);
    const blob = await res.blob();
    // Safari plays a blob URL only with a real audio type; proxies may drop or blur it.
    return blob.type.startsWith('audio/') ? blob : new Blob([blob], { type: 'audio/mpeg' });
  },
  async uploadProjectDocument(
    id: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<ProjectDocument> {
    const form = new FormData();
    form.set('file', file);
    return uploadRequest<ProjectDocument>(`/api/projects/${id}/documents`, form, signal);
  },
  async reindexProjectDocument(projectId: string, documentId: string): Promise<ProjectDocument> {
    return request('POST', `/api/projects/${projectId}/documents/${documentId}/reindex`, {});
  },
  async patchProjectDocument(
    projectId: string,
    documentId: string,
    patch: Partial<{
      displayName: string | null;
      description: string | null;
      tags: string[];
      active: boolean;
    }>,
  ): Promise<ProjectDocument> {
    return request('PATCH', `/api/projects/${projectId}/documents/${documentId}`, patch);
  },
  async getProjectDocumentStatus(
    projectId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<DocumentProcessingStatus> {
    return request(
      'GET',
      `/api/projects/${projectId}/documents/${documentId}/status`,
      undefined,
      signal,
    );
  },
  async getProjectDocumentPreview(
    projectId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<ProjectDocumentPreview> {
    return request(
      'GET',
      `/api/projects/${projectId}/documents/${documentId}/preview`,
      undefined,
      signal,
    );
  },
  async deleteProjectDocument(projectId: string, documentId: string): Promise<void> {
    await request('DELETE', `/api/projects/${projectId}/documents/${documentId}`);
  },
};

export { ApiError };
